import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { invalidateQueriesForResource } from "@/lib/queryCache";

/** Keep linked client reads current across authenticated browser sessions. */
export const useClientRecordsRealtime = () => {
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const orgId = identity?.org_id;
  const memberId = identity?.id;

  useEffect(() => {
    if (orgId == null || memberId == null) return;

    let active = true;
    const refresh = () => {
      if (active) void invalidateQueriesForResource(queryClient, "contacts");
    };
    const channel = supabase.channel(`client-records:${orgId}:${memberId}`);
    for (const table of ["contacts", "companies"]) {
      const filter = `org_id=eq.${orgId}`;
      channel
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table, filter },
          refresh,
        )
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table, filter },
          refresh,
        )
        // DELETE cannot be filtered by Supabase. Consume no deleted-row data:
        // invalidate reads only, whose access remains governed by existing RLS.
        .on(
          "postgres_changes",
          { event: "DELETE", schema: "public", table },
          refresh,
        );
    }
    channel.subscribe((status) => {
      // Also catches up after events missed while disconnected.
      if (status === "SUBSCRIBED") refresh();
    });

    return () => {
      active = false;
      void supabase.removeChannel(channel);
    };
  }, [orgId, memberId, queryClient]);
};
